/**
 * Netlify Forms helper — every public form on the site submits through here.
 *
 * Why: Netlify captures each submission permanently (Site → Forms in the
 * Netlify dashboard) and emails the team through the site's notification
 * hooks. No third-party email service required, nothing silently lost.
 *
 * Each form name used here MUST also exist as a hidden <form> in
 * public/index.html — that is how Netlify learns the form's fields.
 */

export type NetlifyFile = { field: string; blob: Blob; filename: string };

export async function submitNetlifyForm(
  formName: string,
  fields: Record<string, string | number | boolean | null | undefined>,
  files: NetlifyFile[] = []
): Promise<void> {
  const clean: Record<string, string> = { "form-name": formName };
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null) clean[k] = String(v);
  });

  let res: Response;
  if (files.length) {
    // File uploads must go as multipart — let the browser set the boundary header.
    const fd = new FormData();
    Object.entries(clean).forEach(([k, v]) => fd.append(k, v));
    files.forEach((f) => fd.append(f.field, f.blob, f.filename));
    res = await fetch("/", { method: "POST", body: fd });
  } else {
    res = await fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(clean).toString(),
    });
  }
  if (!res.ok) throw new Error(`Netlify form "${formName}" failed: ${res.status}`);
}

/** Turn a base64 payload (as produced by imageUtil.compressImage) back into a Blob. */
export function base64ToBlob(data: string, type = "image/jpeg"): Blob {
  const bin = atob(data);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type });
}
